import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useTreeMutations } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks/TreeMutations/useTreeMutations'
import {
    useNotification,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { getRootArticleContentItemIds } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { mergeContentItems } from 'components/WorkspaceEditor/utils'
import { getActiveTabLabel } from 'components/WorkspaceEditor/utils/workspaceEditorUtils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useShallowSelector } from '../../../Workspace2Store'
import { ContentItemViewNodesType } from '../../../types'
import {
    preventEventDefault,
    tryGetShortcutKey,
} from '../../../utils/shortcutKeyUtils'
import {
    ContentItemViewInput,
    usePromoteDemoteContentViewMutation,
} from '../api/mutations/promoteDemoteContentView'
import {
    INCONSISTENT_PARAGRAPH_LEVEL_ERROR,
    NOT_FOUND,
    NO_ACTIVE_ID,
    PromoteDemoteActions,
} from '../constants'
import {
    usePromoteDemoteSelector,
    usePromoteDemoteStore,
} from '../store/PromoteDemoteStore'
import { PromoteDemoteAction } from '../types'
import { wouldExceedDepthLimit } from '../utils'
import { useFocusRestoration } from './useFocusRestoration'
import { usePromoteDemoteService } from './usePromoteDemoteService'

/**
 * Hook that encapsulates promote/demote functionality for text content elements.
 *
 * @returns An object containing state and functions for promote/demote operations
 */
export const usePromoteDemote = () => {
    const { buildInputVariables } = usePromoteDemoteService()
    const { executeWithOptimistic } = useTransactionManager()
    const { promoteDemoteContentView } = usePromoteDemoteContentViewMutation()
    const { reRenderTree } = useTreeMutations()
    const { restoreFocus } = useFocusRestoration()
    // Captures the active item ID so successCbFn can restore focus after
    // reRenderTree, even though resetPromoteDemote() has already cleared the store.
    const pendingFocusIdRef = useRef<string | undefined>()
    const notification = useNotification()
    const {
        isContentTeamFromStudioPage,
        isEditingPlainText,
        contentItemViewNodes,
        newParagraphToAdd,
        treeData,
        projectId,
        activeTab,
    } = useShallowSelector((s) => ({
        isContentTeamFromStudioPage: s.isContentTeamFromStudioPage,
        isEditingPlainText: s.isEditingPlainText,
        contentItemViewNodes: s.contentItemViewNodes,
        newParagraphToAdd: s.textContentElementToAdd,
        treeData: s.treeData,
        projectId: s.projectId,
        activeTab: s.activeTab,
    }))
    const { id: nodeId } = useElementItemShallowSelector(
        (s) => s.elementItemValue
    )

    const {
        canBePromoted,
        canBeDemoted,
        contentItemToPromoteDemote: activeItem,
        isPromoteDemoteInFlight,
        setPromoteDemoteInFlight,
        setContentItemToPromoteDemote,
        resetPromoteDemoteState: resetPromoteDemote,
    } = usePromoteDemoteSelector((s) => ({
        canBePromoted: s.canBePromoted,
        canBeDemoted: s.canBeDemoted,
        contentItemToPromoteDemote: s.contentItemToPromoteDemote,
        isPromoteDemoteInFlight: s.isPromoteDemoteInFlight,
        setPromoteDemoteInFlight: s.setPromoteDemoteInFlight,
        setContentItemToPromoteDemote: s.setContentItemToPromoteDemote,
        resetPromoteDemoteState: s.resetPromoteDemoteState,
    }))

    const isCreatingNewParagraph = useMemo(() => Boolean(newParagraphToAdd), [
        newParagraphToAdd,
    ])
    const canPromote =
        !isCreatingNewParagraph &&
        !isPromoteDemoteInFlight &&
        canBePromoted(isContentTeamFromStudioPage)
    const canDemote =
        !isCreatingNewParagraph &&
        !isPromoteDemoteInFlight &&
        canBeDemoted(isContentTeamFromStudioPage)

    // Register content for promote/demote operations
    const register = useCallback(
        (
            contentItem: Partial<TextContentElement>,
            isInProject: boolean = false,
            contentItemViewNodes: Partial<ContentItemViewNodesType>[] = []
        ) => {
            setContentItemToPromoteDemote({
                contentItemToPromoteDemote: contentItem,
                isInProject,
                contentItemViewNodes,
            })
        },
        [setContentItemToPromoteDemote]
    )

    // handle mutation request creation and execution
    const handlePromoteDemoteMutation = useCallback(
        async (action: PromoteDemoteAction) => {
            const activeItemId = activeItem?.id

            if (
                isCreatingNewParagraph ||
                usePromoteDemoteStore.getState().isPromoteDemoteInFlight
            )
                return
            if (!activeItemId)
                return notification.error({
                    message: `${NO_ACTIVE_ID} ${action}`,
                })
            if (
                wouldExceedDepthLimit(
                    activeItemId,
                    action,
                    treeData,
                    5,
                    contentItemViewNodes ?? []
                )
            )
                return notification.error(INCONSISTENT_PARAGRAPH_LEVEL_ERROR)

            const variables = buildInputVariables(action)
            if (!variables)
                return notification.error({
                    message: `${NOT_FOUND} ${action}`,
                })

            // Capture ID before resetPromoteDemote() clears the store so
            // successCbFn can restore focus after reRenderTree.
            pendingFocusIdRef.current = activeItemId

            // Capture pre-mutation nodes to use as the merge base. The server
            // returns only the affected article's nodes; merging against the
            // pre-mutation snapshot keeps all other articles intact.
            const currentNodes = contentItemViewNodes
            const rootArticleContentItemIds = getRootArticleContentItemIds(
                currentNodes ?? [],
                [activeItemId]
            )

            // responseNodes is populated by asyncAction before successCbFn runs.
            let responseNodes: Partial<ContentItemViewNodesType>[] = []

            setPromoteDemoteInFlight(true)
            executeWithOptimistic({
                fallbackState: contentItemViewNodes,
                asyncAction: async () => {
                    const contentItemViewInput: ContentItemViewInput = {
                        projectId: projectId ?? '',
                        templateItemPartType: getActiveTabLabel(activeTab) ?? '',
                        isTrackChangeToggleOn: true,
                        rootArticleContentItemId: rootArticleContentItemIds,
                        nodeId: nodeId ?? '',
                        features: [],
                    }
                    const result = await promoteDemoteContentView(
                        variables,
                        contentItemViewInput
                    )
                    responseNodes =
                        result?.data?.promoteDemoteContentView
                            ?.contentItemViewNodes ?? []
                    return result
                },
                successCbFn: () => {
                    resetPromoteDemote()
                    const merged = mergeContentItems(
                        currentNodes ?? [],
                        responseNodes
                    )
                    reRenderTree({ updatedContentItemViewNodes: merged })
                    if (pendingFocusIdRef.current) {
                        restoreFocus(pendingFocusIdRef.current)
                        pendingFocusIdRef.current = undefined
                    }
                    setPromoteDemoteInFlight(false)
                },
                errorCbFn: () => {
                    pendingFocusIdRef.current = undefined
                    setPromoteDemoteInFlight(false)
                },
            })
        },
        [
            executeWithOptimistic,
            promoteDemoteContentView,
            buildInputVariables,
            activeItem,
            contentItemViewNodes,
            isCreatingNewParagraph,
            treeData,
            notification,
            setPromoteDemoteInFlight,
            projectId,
            activeTab,
            nodeId,
        ]
    )

    // handle keyboard events for promote/demote shortcuts
    const handlePromoteDemoteShortcutKey = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            const { Promote, Demote } = PromoteDemoteActions

            if (tryGetShortcutKey(Promote, event) && canPromote) {
                // Prevent default Shift+Tab behavior to stop focus from moving
                preventEventDefault(event)
                handlePromoteDemoteMutation(Promote)
            } else if (tryGetShortcutKey(Demote, event) && canDemote) {
                // Prevent default Tab behavior to stop focus from moving to next element
                preventEventDefault(event)
                handlePromoteDemoteMutation(Demote)
            }
        },
        [canPromote, canDemote, handlePromoteDemoteMutation]
    )

    /**
     * side-effect: reset promote/demote state when nothing is selected
     * Toggles the Promote/Demote buttons on action bar
     */
    useEffect(() => {
        let resetId: NodeJS.Timeout

        if (!isEditingPlainText && activeItem) {
            resetId = setTimeout(() => {
                // reset means toggle the promote/demote buttons on action bar
                resetPromoteDemote()
            }, 100) // 100ms delay to ensure that the current item is evaluated by action bar before resetting the state
        }

        return () => clearTimeout(resetId)
    }, [isEditingPlainText, activeItem, resetPromoteDemote])

    return {
        canPromote,
        canDemote,
        isPromoteDemoteInFlight,
        activeItem,
        register,
        handlePromoteDemoteShortcutKey,
        handlePromoteDemoteMutation,
        resetPromoteDemote,
    }
}
